// @ts-strict-ignore
import { Injectable, ViewContainerRef } from '@angular/core';
import { SohoModalDialogRef, SohoModalDialogService } from 'ids-enterprise-ng';
import { LMCompactFormDialogComponent } from './lm-compact-form-dialog.component';
import { AppConstants, LMDialogButtonKey, LMFormType } from '@utils/app.constants';
import { LMModel } from '@form/model';
import { Observable } from 'rxjs';
import { LMCommonUtilService, LMUniqueIdService } from '@utils/index';
import { LMBusyIndicatorService, LMFormatUtilService, LMUrlInfoBuilderService } from '@services/index';
import { LMFormSpecUtilService } from '@form/services';

export enum LMCompactFormDialogResult {
  RESPONSE_CANCEL = 0,
  RESPONSE_OK
}

export interface LMCompactFormResult {
  result: LMCompactFormDialogResult;
  fieldId?: string;
  dataView?: LMDataView;
  model?: LMModel;
  bindIds?: Array<string>;
  filterCriteria?: Array<LMFilterFieldCriteria>;
}

export interface LMCompactFormDialogOptions {
  placeholder: ViewContainerRef;
  fieldId: string;
  layout: LMFormLayout;
  title: LMMessageRule;
  dataView: LMDataView;
  formUrlInfo: LMFormUrlInfo,
  formSpec: LMFormSpec;
  specFields?: LMSpecFields;
  model?: LMModel;
  originalCompoundFieldLayout?: LMCompoundFieldLayout;
  displayOnly?: boolean;
  formType?: LMFormType;
  formOptions?: LMFormOptions;
  excludeOriginalField?: boolean;
  fromFilter?: boolean;
}

@Injectable()
export class LMCompactFormDialogService {
  constructor(
    private sohoModalDialogService: SohoModalDialogService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmCommonUtilService: LMCommonUtilService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmFormSpecUtilService: LMFormSpecUtilService,
  ) {}

  private buttons: SohoModalButton[];
  private dlgComponent: LMCompactFormDialogComponent;

  public promptCompactFormDialog(args: LMCompactFormDialogOptions): Observable<LMCompactFormResult> {
    const formSpec: LMFormSpec = this.buildCompactFormDlgParams(args);
    const formUrlInfo = args.formUrlInfo;
    const specFields: LMSpecFields = formSpec.busFormSpec.fields;
    return this.openDialog(formUrlInfo, formSpec, args.dataView, args.placeholder, args.fieldId, args.title, specFields, args.model || null, args.formOptions || {});
  }

  private buildCompactFormDlgParams(args: LMCompactFormDialogOptions): LMFormSpec {
    // make a deep clone of the formSpec
    const formSpec: LMFormSpec = JSON.parse(JSON.stringify(args.formSpec));
    formSpec.automationId = args.fieldId;

    // update with the correct subFormType
    formSpec.busFormSpec.subFormType = AppConstants.FORMSUBTYPE_COMPOUNDFLD;

    // TODO this seems fragile to need to reset all these attributes. Can't we just drive off  'formSpec.busFormSpec.subFormType = AppConstants.FORMSUBTYPE_COMPOUNDFLD'
    formSpec.busFormSpec.busFormLayoutSpec = { layout: null };
    formSpec.busFormSpec.panels = undefined;

    if (formSpec.busFormSpec.isWizard) {
      formSpec.busFormSpec.isWizard = false;
    }

    formSpec.busFormSpec.formType = (args.formType) ? args.formType : LMFormType.STANDARD;

    // keep the original namePrefix, name, and current namePrefix
    formSpec.busFormSpec.namePrefix = this.lmUniqueIdService.uniqueId([ args.formSpec.busFormSpec.namePrefix, args.formSpec.busFormSpec.name, formSpec.busFormSpec.namePrefix ]);

    // replace the layout with with a deep clone of the compact form layout
    formSpec.busFormSpec.busFormLayoutSpec.layout = JSON.parse(JSON.stringify(args.layout));

    if (args.displayOnly) {
      formSpec.isReadOnly = true;
    }

    // replace with a deep clone
    if (args.originalCompoundFieldLayout && !args.excludeOriginalField) {
      const originalCompoundField = JSON.parse(JSON.stringify(args.originalCompoundFieldLayout));
      originalCompoundField.previewMode = true;
      const originalCompoundFieldLayout: LMFormLayoutSection = { field: originalCompoundField };

      formSpec.busFormSpec.busFormLayoutSpec.layout.sections.unshift(originalCompoundFieldLayout);
    }

    // if specFields were passed, they need to be integrated into the busFormSpec.fields collection
    // if a specField already exists (same bindId), then the incoming specField replaces it; otherwise, it is added
    if (args.specFields) {
      const currentSpecFields: LMSpecFields = formSpec.busFormSpec.fields;

      Object.keys(args.specFields).forEach((key: string) => {
        currentSpecFields[ key ] = args.specFields[ key ];
      });
    }

    return formSpec;
  }

  private openDialog(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    dataView: LMDataView,
    placeholder: ViewContainerRef,
    fieldId: string,
    title: LMMessageRule,
    specFields: LMSpecFields,
    parentModel?: LMModel,
    formOptions?: LMFormOptions
  ): Observable<LMCompactFormResult> {
    this.lmBusyIndicatorService.showBusyIndicator();

    return new Observable<LMCompactFormResult>(observer => {
      let dialog: SohoModalDialogRef<LMCompactFormDialogComponent>;
      const bindIds = this.lmCommonUtilService.getBindIdsFromFormSpec(formSpec);
      const formNamePrefix = (formSpec.busFormSpec.namePrefix) ? formSpec.busFormSpec.namePrefix : '';
      const formName = formSpec.busFormSpec.name;
      const isCodeBlockFilter = this.lmFormSpecUtilService.isCodeBlockFilter(formSpec);
      const isList = formSpec.selectViewType === 'list';
      const okButton = this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.OK ]);
      const clearButton = this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.CLEAR ]);

      const buttonIds = {
        ok: okButton,
        clear: clearButton
      };

      this.buttons = [];

      if (isCodeBlockFilter) {
        this.buttons.push({
          id: clearButton,
          text: Soho.Locale.translate(LMDialogButtonKey.CLEAR),
          click: () => {
            this.clearForm(formSpec, dataView);
          },
          tabindex: -1
        });
      }

      this.buttons.push({
        id:        okButton,
        text:      Soho.Locale.translate(LMDialogButtonKey.OK),
        validate:  false,
        isDefault: true,
        click:     (e, modal) => {
          const filterCriteria = (isCodeBlockFilter) ? this.dlgComponent.formComponent.formContentComponent.searchFormComponent.getFilterCriteria() : [];
          this.dlgComponent.formComponent.waitForFormToBeReady().subscribe((status: boolean) => {
            dialog.close(<LMCompactFormResult>{
              result:   LMCompactFormDialogResult.RESPONSE_OK,
              fieldId,
              dataView: this.dlgComponent.formComponent.formModel.getData(),
              model:    this.dlgComponent.formComponent.model,
              bindIds,
              filterCriteria
            });
          });
        }
      });

      const attributes: SohoAttributesArray = [ {
        name: 'data-automation-id',
        value: `lm-${fieldId}-modal`
      } ];

      // Handle if the title is a message rule
      const modalTitle = this.lmFormatUtilService.formatMessageRule(title, dataView, specFields);

      const sohoModalOptions: SohoModalOptions = {
        title:         modalTitle,
        isAlert:       false,
        fullsize:      'responsive',
        showCloseBtn:  true,
        attributes,
        buttons:       this.buttons,
        closeBtnOptions: isList ? {
          attributes: [{ name: 'tabIndex', value: '-1' }]
        } : undefined
      };

      dialog = this.sohoModalDialogService
        .modal(LMCompactFormDialogComponent, placeholder)
        .options(sohoModalOptions)
        .beforeOpen(() => {
          this.lmBusyIndicatorService.hideBusyIndicator();
          return true;
        })
        .open();

      dialog.apply((compactForm: LMCompactFormDialogComponent) => {
        this.dlgComponent = compactForm;

        compactForm.formOptions = formOptions;
        compactForm.formUrlInfo = formUrlInfo;
        compactForm.formSpec = formSpec;
        compactForm.dataView = dataView;
        compactForm.specFields = specFields;
        compactForm.parentModel = parentModel;
        compactForm.buttonIds = buttonIds;
        compactForm.defaultButton = this.buttons.find( button => button.isDefault );
      });

      dialog.afterClose(result => {
        if (!result) {
          result = <LMCompactFormResult>{
            result: LMCompactFormDialogResult.RESPONSE_CANCEL
          };
        }

        observer.next(result);
        observer.complete();
      });
    });
  }

  private clearForm(formSpec: LMFormSpec, dataView: LMDataView) {
    this.lmCommonUtilService.clearCodeBlockDataViewFields(formSpec, formSpec.busFormSpec.busFormLayoutSpec.layout, dataView);
    this.dlgComponent.clearForm(dataView);
  }
}
